import { Template, Slot, MediaType } from '../../core/template.schemas';
import { EntitySlotSelectionMap, EntitySlotSelection, EntitySlotUtils, SelectorSelectionMap } from '../types/PostGenerationTypes';

/**
 * Service for analyzing templates and extracting slot-based bindings
 */
export class TemplateAnalysisService {
  /**
   * Analyzes template and extracts all slot-based bindings
   */
  static analyzeTemplate(template: Template): TemplateAnalysisResult {
    const slotBindings: SlotBinding[] = [];
    const selectorBindings: SelectorBinding[] = [];
    const slotUsageMap = new Map<string, SlotUsageData>();
    const selectorUsageMap = new Map<string, SelectorUsageData>();

    // Analyze all elements for slot bindings
    template.elements.forEach(element => {
      this.analyzeElement(element, slotBindings, slotUsageMap, selectorBindings, selectorUsageMap);
    });

    // Convert to EntitySlotSelectionMap
    const entitySlots = this.createEntitySlotMap(template.slots || [], slotUsageMap);
    const selectors = this.createSelectorMap(selectorUsageMap);

    return {
      slotBindings,
      selectorBindings,
      entitySlots,
      selectors,
      hasSlotBindings: slotBindings.length > 0,
      hasSelectorBindings: selectorBindings.length > 0,
      requiredSlots: this.getRequiredSlots(entitySlots),
      optionalSlots: this.getOptionalSlots(entitySlots)
    };
  }

  /**
   * Analyzes a single element for slot bindings
   */
  private static analyzeElement(
    element: any,
    bindings: SlotBinding[],
    slotUsageMap: Map<string, SlotUsageData>,
    selectorBindings: SelectorBinding[],
    selectorUsageMap: Map<string, SelectorUsageData>
  ): void {
    // Check text content
    if (element.type === 'text') {
      if (this.hasSlotBinding(element.content)) {
        this.processSlotBinding(element, 'content', element.content, bindings, slotUsageMap);
      }
      if (this.hasSelectorBinding(element.content)) {
        this.processSelectorBinding(element, 'content', element.content, selectorBindings, selectorUsageMap);
      }
    }

    // Check image source
    if (element.type === 'image') {
      if (this.hasSlotBinding(element.source)) {
        this.processSlotBinding(element, 'source', element.source, bindings, slotUsageMap);
      }
      if (this.hasSelectorBinding(element.source)) {
        this.processSelectorBinding(element, 'source', element.source, selectorBindings, selectorUsageMap);
      }
    }

    // Check color properties
    if (this.hasSlotBinding(element.color)) {
      this.processSlotBinding(element, 'color', element.color, bindings, slotUsageMap);
    }
    if (this.hasSelectorBinding(element.color)) {
      this.processSelectorBinding(element, 'color', element.color, selectorBindings, selectorUsageMap);
    }

    // Check fill color for shapes
    if (element.fill) {
      if (this.hasSlotBinding(element.fill.color)) {
        this.processSlotBinding(element, 'fill.color', element.fill.color, bindings, slotUsageMap);
      }
      if (this.hasSelectorBinding(element.fill.color)) {
        this.processSelectorBinding(element, 'fill.color', element.fill.color, selectorBindings, selectorUsageMap);
      }
    }
  }

  /**
   * Checks if a value contains a slot binding
   */
  private static hasSlotBinding(value: any): boolean {
    return value?.mode === 'dynamic' && value.dynamic?.source?.kind === 'slot';
  }

  /**
   * Checks if a value contains a selector binding
   */
  private static hasSelectorBinding(value: any): boolean {
    return value?.mode === 'dynamic' && (value.dynamic?.source as any)?.kind === 'selector';
  }

  /**
   * Processes a slot binding and updates tracking data
   */
  private static processSlotBinding(
    element: any,
    attribute: string,
    binding: any,
    bindings: SlotBinding[],
    slotUsageMap: Map<string, SlotUsageData>
  ): void {
    const source = binding.dynamic.source;
    const bindingId = `${element.id}-${attribute}`;

    // Create slot binding
    const slotBinding: SlotBinding = {
      id: bindingId,
      elementId: element.id,
      elementType: element.type,
      elementAttribute: attribute,
      slotId: source.slotId,
      attributePath: source.attributePath,
      mediaType: binding.mediaType,
      format: binding.format,
      defaultValue: binding.dynamic.defaultValue,
      required: true // Default to required, can be made configurable
    };

    bindings.push(slotBinding);

    // Track slot usage
    if (!slotUsageMap.has(source.slotId)) {
      slotUsageMap.set(source.slotId, {
        slotId: source.slotId,
        bindingIds: [],
        attributePaths: new Set(),
        required: true
      });
    }

    const usage = slotUsageMap.get(source.slotId)!;
    usage.bindingIds.push(bindingId);
    usage.attributePaths.add(source.attributePath);
  }

  /**
   * Processes a selector binding and updates tracking data
   */
  private static processSelectorBinding(
    element: any,
    attribute: string,
    binding: any,
    bindings: SelectorBinding[],
    selectorUsageMap: Map<string, SelectorUsageData>
  ): void {
    const source = binding.dynamic.source as { kind: 'selector'; selectorId: string };
    const bindingId = `${element.id}-${attribute}`;

    const selectorBinding: SelectorBinding = {
      id: bindingId,
      elementId: element.id,
      elementType: element.type,
      elementAttribute: attribute,
      selectorId: source.selectorId,
      mediaType: binding.mediaType as MediaType,
      format: binding.format,
      defaultValue: binding.dynamic.defaultValue,
      required: true
    };

    bindings.push(selectorBinding);

    if (!selectorUsageMap.has(source.selectorId)) {
      selectorUsageMap.set(source.selectorId, {
        selectorId: source.selectorId,
        bindingIds: [],
        mediaType: binding.mediaType as MediaType,
        required: true,
        format: binding.format,
        defaultValue: binding.dynamic.defaultValue,
        label: binding.dynamic.label || source.selectorId,
      });
    } else {
      const usage = selectorUsageMap.get(source.selectorId)!;
      // Keep first mediaType; warn if mismatch
      if (usage.mediaType !== binding.mediaType) {
        console.warn(`Selector '${source.selectorId}' used with different media types:`, usage.mediaType, 'vs', binding.mediaType);
      }
    }

    const usage = selectorUsageMap.get(source.selectorId)!;
    usage.bindingIds.push(bindingId);
  }

  /**
   * Creates EntitySlotSelectionMap from template slots and usage data
   */
  private static createEntitySlotMap(
    templateSlots: Slot[],
    slotUsageMap: Map<string, SlotUsageData>
  ): EntitySlotSelectionMap {
    const entitySlots: EntitySlotSelectionMap = {};

    templateSlots.forEach(slot => {
      const usage = slotUsageMap.get(slot.id);
      const isUsed = Boolean(usage);
      
      if (isUsed) {
        const slotKey = EntitySlotUtils.generateSlotKey(slot.entityType, slot.name);
        
        entitySlots[slotKey] = {
          slotKey,
          entityType: slot.entityType,
          slotName: slot.name,
          displayLabel: slot.name || slot.entityType,
          selectedInstance: null,
          required: usage!.required,
          bindingIds: usage!.bindingIds,
          slotId: slot.id,
          attributePaths: Array.from(usage!.attributePaths)
        };
      }
    });

    return entitySlots;
  }

  /**
   * Creates SelectorSelectionMap from selector usage
   */
  private static createSelectorMap(
    selectorUsageMap: Map<string, SelectorUsageData>
  ): SelectorSelectionMap {
    const selectors: SelectorSelectionMap = {};
    selectorUsageMap.forEach(usage => {
      selectors[usage.selectorId] = {
        selectorId: usage.selectorId,
        displayLabel: usage.label || usage.selectorId,
        mediaType: usage.mediaType,
        selectedValue: usage.defaultValue ?? null,
        required: usage.required,
        bindingIds: usage.bindingIds,
        format: usage.format,
        defaultValue: usage.defaultValue,
      };
    });
    return selectors;
  }

  /**
   * Gets all required slots from entity slot map
   */
  private static getRequiredSlots(entitySlots: EntitySlotSelectionMap): EntitySlotSelection[] {
    return Object.values(entitySlots).filter(slot => slot.required);
  }

  /**
   * Gets all optional slots from entity slot map
   */
  private static getOptionalSlots(entitySlots: EntitySlotSelectionMap): EntitySlotSelection[] {
    return Object.values(entitySlots).filter(slot => !slot.required);
  }
}

/**
 * Result of template analysis
 */
export interface TemplateAnalysisResult {
  /** All slot bindings found in template */
  slotBindings: SlotBinding[];
  /** All selector bindings found in template */
  selectorBindings: SelectorBinding[];
  /** Entity slots mapped for selection */
  entitySlots: EntitySlotSelectionMap;
  /** Selector selections mapped for input */
  selectors: SelectorSelectionMap;
  /** Whether template has any slot bindings */
  hasSlotBindings: boolean;
  /** Whether template has any selector bindings */
  hasSelectorBindings: boolean;
  /** Required slots that must be filled */
  requiredSlots: EntitySlotSelection[];
  /** Optional slots that can be filled */
  optionalSlots: EntitySlotSelection[];
}

/**
 * Represents a slot binding in an element
 */
export interface SlotBinding {
  /** Unique binding identifier */
  id: string;
  /** Element ID containing this binding */
  elementId: string;
  /** Type of element */
  elementType: string;
  /** Element attribute that contains the binding */
  elementAttribute: string;
  /** Referenced slot ID */
  slotId: string;
  /** Attribute path within the slot entity */
  attributePath: string;
  /** Media type of the binding */
  mediaType: string;
  /** Optional formatting configuration */
  format?: any;
  /** Optional default value */
  defaultValue?: any;
  /** Whether this binding is required */
  required: boolean;
}

/**
 * Represents a selector binding in an element
 */
export interface SelectorBinding {
  /** Unique binding identifier */
  id: string;
  /** Element ID containing this binding */
  elementId: string;
  /** Type of element */
  elementType: string;
  /** Element attribute that contains the binding */
  elementAttribute: string;
  /** Referenced selector ID */
  selectorId: string;
  /** Media type of the binding */
  mediaType: MediaType;
  /** Optional formatting configuration */
  format?: any;
  /** Optional default value */
  defaultValue?: any;
  /** Whether this binding is required */
  required: boolean;
}

/**
 * Internal slot usage tracking data
 */
interface SlotUsageData {
  slotId: string;
  bindingIds: string[];
  attributePaths: Set<string>;
  required: boolean;
}

/**
 * Internal selector usage tracking data
 */
interface SelectorUsageData {
  selectorId: string;
  bindingIds: string[];
  mediaType: MediaType;
  required: boolean;
  format?: any;
  defaultValue?: any;
  label?: string;
}